// Get job interview questions **************************
// pathname: /v1/job-interview/interview-questions
// ******************************************************
import { useSwrApi } from '@hooks/useSwrApi';
import { ApiQueryParams } from '@root/src/types/api/apiUtilityFn';
import { buildSearchParams } from '@root/src/utils';

/** Props */
type JobInterviewQuestionsProps = {
  locale: Locale;
  shouldFetch?: boolean;
  payload?: ApiSchema<'UserInterviewQuestionsRequest'>;
  queryParams?: ApiQueryParams<'/v1/job-interview/interview-questions', 'post'>;
  serverSideData?: ApiSchema<'InterviewQuestionListResult'>;
};

/**
 * @description Get job interview questions.
 * - `locale`: *Locale* - The current app locale.
 * - `shouldFetch`: *boolean* - Should fetch data. Only explicit 'false' disables fetching.
 * - `payload`: *object* - The payload for the request, if any.
 * - `queryParams`: *object* - The query parameters for the request.
 */

export const useSwrJobInterviewQuestions = (props: JobInterviewQuestionsProps) => {
  const {
    locale = 'ro',
    shouldFetch = true,
    payload = {},
    queryParams = {
      locale,
      limit: 24,
      page: 1,
    },
    serverSideData,
  } = props;

  // Get data from API
  // ********************************************
  const {
    data, error, isLoading, isValidating, mutate,
  } = useSwrApi<ApiSchema<'InterviewQuestionListResult'>>({
    url: '/v1/job-interview/interview-questions',
    locale,
    shouldFetch,
    fetcherConfig: {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      payload,
      stringifyPayload: true,
    },
    urlParams: buildSearchParams(queryParams, '?', false),
    apiOptions: {
      dedupingInterval: 10 * 1000, // 10 seconds
      keepPreviousData: true,
      revalidateOnFocus: false,
    },
    serverSideData,
  });

  // Return data
  // ********************************************
  return {
    data,
    error,
    mutate,
    isLoading,
    isValidating,
  };
};
